import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import {
  fetchRoleById,
  clearSelectedEntity,
  selectSelectedRole,
  selectRoleDetailStatus,
  selectRoleDetailError,
} from '../../features/roles/roleSlice';
import { selectUserInformations } from '../../features/authSlice';
import { showToast } from '../../utils/utils';
import Loader from '../../components/Live/Loader';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

/**
 * Preview Role Page
 * Full page read-only view of role details with permissions
 */
const PreviewRolePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);

  const selectedRole = useSelector(selectSelectedRole);
  const detailStatus = useSelector(selectRoleDetailStatus);
  const detailError = useSelector(selectRoleDetailError);

  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchRoleById({ token: user.token, roleId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate('/dashboard/roles', { replace: true });
    }
  }, [detailStatus, detailError, navigate]);

  const loading = detailStatus === 'loading';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate('/dashboard/roles')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Roles
          </button>
        </div>
      </div>
    );
  }

  if (!selectedRole) {
    return null;
  }

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard/roles"
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white capitalize">
                  Role Details
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  View role information and permissions
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <RoleDetailsContent role={selectedRole} />
      </div>
    </div>
  );
};

// Role Details Content Component (extracted from RolePreview)
const RoleDetailsContent = ({ role }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [groupedPermissions, setGroupedPermissions] = React.useState({});
  const [openModules, setOpenModules] = React.useState(new Set());

  // Group permissions by module
  React.useEffect(() => {
    if (role?.permissions) {
      const groups = role.permissions.reduce((acc, permission) => {
        const [module] = permission.name.split('_');
        acc[module] = acc[module] || [];
        acc[module].push(permission);
        return acc;
      }, {});
      setGroupedPermissions(groups);
      setOpenModules(new Set(Object.keys(groups)));
    }
  }, [role]);

  // Toggle accordion
  const toggleModule = (module) => {
    setOpenModules(prev => {
      const next = new Set(prev);
      next.has(module) ? next.delete(module) : next.add(module);
      return next;
    });
  };

  // Filtered and grouped permissions
  const filteredPermissions = React.useMemo(() => {
    if (!searchQuery) return groupedPermissions;
    return Object.entries(groupedPermissions).reduce((acc, [module, perms]) => {
      const filtered = perms.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (filtered.length > 0) acc[module] = filtered;
      return acc;
    }, {});
  }, [groupedPermissions, searchQuery]);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: "easeInOut" }
    }
  };

  const moduleVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.2 }
    }
  };

  const permissionItemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05 }
    })
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Metadata Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoCard label="UUID" value={role?.uuid} />
        <InfoCard label="Name" value={role?.name} />
        <InfoCard label="Created At" value={role?.created_at} />
        <InfoCard label="Count Of User Has This Role" value={role?.users_count} />
      </div>

      {/* Permissions Section */}
      <div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
            Permissions ({role?.permissions?.length || 0})
          </h4>
          <div className="w-full sm:w-64">
            <input
              type="text"
              placeholder="Search permissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <LayoutGroup>
          <div className="border rounded-lg overflow-hidden dark:border-gray-700">
            <AnimatePresence>
              {Object.entries(filteredPermissions).map(([module, permissions]) => (
                <motion.div
                  key={module}
                  className="border-b last:border-b-0 dark:border-gray-700"
                  variants={moduleVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                >
                  <button
                    onClick={() => toggleModule(module)}
                    className="w-full flex justify-between items-center bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 px-4 py-3 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-gray-600 dark:text-gray-300">
                        {openModules.has(module) ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                      </span>
                      <h5 className="font-medium text-gray-700 dark:text-gray-200 capitalize">{module}</h5>
                      <span className="text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded">
                        {permissions.length} permissions
                      </span>
                    </div>
                  </button>

                  <AnimatePresence>
                    {openModules.has(module) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{
                          opacity: 1,
                          height: "auto",
                          transition: { duration: 0.3 }
                        }}
                        exit={{
                          opacity: 0,
                          height: 0,
                          transition: { duration: 0.2 }
                        }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 ">
                          {permissions.map((permission, index) => (
                            <motion.div
                              key={permission.uuid}
                              variants={permissionItemVariants}
                              custom={index}
                              initial="hidden"
                              animate="visible"
                              exit="hidden"
                            >
                              <PermissionBadge permission={permission} />
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>

            <AnimatePresence>
              {Object.keys(filteredPermissions).length === 0 && (
                <motion.div
                  className="p-6 text-center text-gray-500 dark:text-gray-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  No permissions found matching your search
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </LayoutGroup>
      </div>
    </motion.div>
  );
};

// Sub-components
const InfoCard = ({ label, value }) => (
  <motion.div
    className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
  >
    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 font-medium">{value}</dd>
  </motion.div>
);

const PermissionBadge = ({ permission }) => {
  const [, action] = permission.name.split('_');
  const colorClasses = [
    'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
    'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',
    'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
    'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
    'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800',
  ];

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % colorClasses.length;
  };

  const colorClass = colorClasses[hashString(action)] || colorClasses[0];

  return (
    <motion.div
      className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-700 transition-colors"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <span className={`px-2 py-1 rounded text-xs font-medium border ${colorClass}`}>
        {action}
      </span>
      <span className="text-sm text-gray-600 dark:text-gray-300 flex-1">
        {permission.name.replace(/_/g, ' ')}
      </span>
    </motion.div>
  );
};

export default PreviewRolePage;
